import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { KeyRound, Copy, RefreshCw, Trash2, Server } from "lucide-react";

export default function AdminExternalSync() {
    const [cfg, setCfg] = useState(null);
    const [showToken, setShowToken] = useState(null);   // holds one-time visible token
    const [rotating, setRotating] = useState(false);

    const load = () => api.get("/admin/config").then(({ data }) => setCfg(data));
    useEffect(() => { load(); }, []);

    const toggleEnabled = async (v) => {
        try {
            await api.put("/admin/config", { external_sync_enabled: v });
            toast.success(v ? "Sync esterno ATTIVO" : "Sync esterno DISATTIVATO");
            load();
        } catch { toast.error("Errore"); }
    };

    const rotate = async () => {
        setRotating(true);
        try {
            const { data } = await api.post("/admin/external-sync/token/rotate");
            setShowToken(data.token);
            toast.success("Token generato — copialo subito");
            load();
        } catch { toast.error("Errore"); } finally { setRotating(false); }
    };

    const revoke = async () => {
        if (!window.confirm("Revocare il token? Le richieste esterne smetteranno di funzionare finché non ne generi uno nuovo.")) return;
        try {
            await api.delete("/admin/external-sync/token");
            setShowToken(null);
            toast.success("Token revocato");
            load();
        } catch { toast.error("Errore"); }
    };

    const copy = async () => {
        if (!showToken) return;
        try {
            await navigator.clipboard.writeText(showToken);
            toast.success("Copiato negli appunti");
        } catch { toast.error("Copia manuale"); }
    };

    if (!cfg) return <div className="text-[hsl(var(--muted-foreground))]">Caricamento…</div>;

    const base = process.env.REACT_APP_BACKEND_URL;

    return (
        <div className="max-w-3xl" data-testid="admin-external-sync-page">
            <h1 className="text-3xl mb-2">Sync Prezzi Gestionale Esterno</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6 max-w-xl">
                Endpoint inbound protetto da token API. Aggiorna solo <code>base_price_public</code>
                del formato tramite matching su <code>mexal_sku</code>. Price override lato admin
                vince sempre.
            </p>

            <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-5 mb-6">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <div className="text-lg">Sync esterno</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))]">
                            Quando disattivo, l'endpoint <code>POST /api/sync/prices</code> risponde 403.
                        </div>
                    </div>
                    <Switch
                        checked={cfg.external_sync_enabled}
                        onCheckedChange={toggleEnabled}
                        data-testid="external-sync-enabled-toggle"
                    />
                </div>
            </div>

            <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-5 mb-6 space-y-4">
                <div className="flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} />
                    <h2 className="text-xl">Token API</h2>
                </div>
                <div className="text-sm">
                    <div className="mb-2">
                        Stato: <span className="font-medium">{cfg.external_sync_token_set ? "impostato" : "assente"}</span>
                        {cfg.external_sync_token_set && (
                            <span className="ml-3 font-mono text-xs text-[hsl(var(--muted-foreground))]" data-testid="external-token-masked">
                                {cfg.external_sync_token_masked}
                            </span>
                        )}
                    </div>
                    {showToken && (
                        <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-sm mt-3" data-testid="external-sync-fresh-token">
                            <div className="text-xs uppercase tracking-widest text-yellow-800 mb-1">Token appena generato · copialo ora</div>
                            <div className="flex items-center gap-2">
                                <code className="text-sm font-mono break-all flex-1">{showToken}</code>
                                <button type="button" onClick={copy} className="co-btn-ghost text-xs">
                                    <Copy className="w-3.5 h-3.5" /> Copia
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex gap-2 pt-2">
                    <button type="button" onClick={rotate} disabled={rotating} className="co-btn-primary" data-testid="rotate-token-btn">
                        <RefreshCw className="w-4 h-4" /> {rotating ? "Generazione…" : (cfg.external_sync_token_set ? "Ruota token" : "Genera token")}
                    </button>
                    {cfg.external_sync_token_set && (
                        <button type="button" onClick={revoke} className="co-btn-ghost text-[hsl(var(--destructive))]" data-testid="external-sync-revoke-btn">
                            <Trash2 className="w-4 h-4" /> Revoca
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                    <Server className="w-4 h-4 text-[hsl(var(--muted-foreground))]" strokeWidth={1.5} />
                    <h2 className="text-xl">Come chiamare l'endpoint</h2>
                </div>
                <pre className="text-xs font-mono bg-[hsl(var(--secondary))] p-3 rounded-sm overflow-x-auto whitespace-pre">
{`POST ${base}/api/sync/prices
Authorization: Bearer <TOKEN>
Content-Type: application/json

[
  {"sku": "00001", "price": 950.00},
  {"sku": "000601", "price": 28.50}
]`}
                </pre>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3">
                    La risposta include <code>{`{updated, unmapped[]}`}</code>. SKU non mappati vengono ignorati.
                </p>
            </div>
        </div>
    );
}
